import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext.tsx";
import { skillCategories, skills } from "../data/index.ts";

const SkillsSection = () => {
  const { translations } = useLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const getSkillItems = (categoryId: string): string[] => {
    if (categoryId === "softSkills") {
      return skills.softSkillKeys.map(
        (key) => translations.skills.softSkillItems[key]
      );
    }

    const items = skills[categoryId as keyof typeof skills];
    if (!Array.isArray(items)) return [];

    return items.map((item) =>
      typeof item === "string" ? item : item.name
    );
  };

  return (
    <section id="skills" ref={sectionRef} className="py-20">
      <motion.div className="container mx-auto px-4 md:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-dark-900 dark:text-white mb-4">
            {translations.skills.title}
          </h2>
          <div className="w-20 h-1.5 bg-primary-500 rounded-full mx-auto" />
          <p className="mt-4 text-dark-700 dark:text-dark-100 max-w-2xl mx-auto">
            {translations.skills.subtitle}
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {skillCategories.map((category) => (
            <motion.div
              key={category.id}
              className="bg-white dark:bg-dark-800 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-100 dark:border-dark-700"
              variants={itemVariants}
            >
              <div
                className={`mb-4 ${category.color === "secondary" ? "text-secondary-500" : "text-primary-500"}`}
              >
                <i className={`fa-solid ${category.icon} text-3xl`} />
              </div>
              <h3 className="text-xl font-heading font-bold text-dark-900 dark:text-white mb-4">
                {translations.skills.sections[category.id]}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {getSkillItems(category.id).map((name) => (
                  <div className="skill-chip" key={name}>
                    <span className="px-3 py-1 bg-gray-100 dark:bg-dark-700 text-dark-700 dark:text-dark-200 rounded-md font-medium">
                      {name}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default SkillsSection;
